import { Playbook } from '@/types/playbook/playbook';
import { Bot, Calendar, CheckCircle, Clock, Mail, User } from 'lucide-react';

export const demoScheduler: Playbook = {
  meta: {
    id: 'demo-scheduler',
    slug: 'demo-scheduler',
    categoryId: 'capture-leads',
    name: 'The Demo Scheduler',
    tagline: 'Book meetings directly into calendars.',
    description: 'Let a Genie qualify leads and schedule product demos directly into your sales team\'s calendar with intelligent matching.',
    strategy: 'Let the Genie qualify leads and schedule product demos directly into your sales team\'s calendar.',
    benefits: [
      'Automated lead qualification',
      'Direct calendar integration',
      'Smart expert matching',
      'Reduced scheduling friction'
    ],
    targetAudience: ['B2B SaaS', 'Enterprise software', 'Professional services'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'intermediate',
    tags: ['demo-booking', 'calendar-integration', 'lead-qualification', 'sales'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Demo Scheduler Playbook - Genie-Powered Meeting Booking',
    metaDescription: 'Automate demo scheduling with Genie qualification and direct calendar booking. Match leads with the right experts instantly.',
    keywords: ['demo scheduling', 'meeting booking', 'lead qualification', 'calendar integration', 'sales automation']
  },
  demoConfig: {
    triggerType: 'button',
    message: "I can help you schedule a personalized demo with our team. First, let me understand your needs to match you with the right expert.",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Let's schedule your demo! What's your main use case?" },
    { speaker: 'user', message: "We need it for customer support automation" },
    { speaker: 'agent', message: "Perfect! Sarah specializes in support solutions. She has slots tomorrow at 2pm or Thursday at 10am." },
    { speaker: 'user', message: "Thursday works" },
    { speaker: 'agent', message: "Excellent! I've booked it. You'll receive a calendar invite shortly." }
  ],
  templateConfig: {
    firstMessage: "Ready for a personalized demo? Let me help you schedule time with the right expert.",
    systemPrompt: "You are a demo scheduling Genie. Qualify the lead's needs, match them with the appropriate team member, and book meetings efficiently.",
    tools: ['calendar_booking', 'email_capture', 'lead_qualification']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Lead requests demo',
      description: 'Potential customer expresses interest in seeing product demonstration'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Qualifies lead needs',
      description: 'Genie gathers information about use case, company size, and specific requirements'
    },
    {
      type: 'action',
      icon: User,
      genieIcon: 'target',
      label: 'Matches with expert',
      description: 'Intelligently assigns to sales rep or specialist based on lead profile'
    },
    {
      type: 'action',
      icon: Calendar,
      genieIcon: 'calendar-check',
      label: 'Shows available slots',
      description: 'Displays real-time calendar availability for matched team member'
    },
    {
      type: 'action',
      icon: Clock,
      genieIcon: 'calendar-check',
      label: 'Books meeting instantly',
      description: 'Confirms time slot and sends calendar invites to both parties'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Qualified demo scheduled',
      description: 'High-quality lead matched with right expert and meeting confirmed'
    }
  ],
  metrics: {
    conversionRate: '52%',
    responseTime: '<2 seconds',
    satisfactionScore: '4.7/5',
    costSavings: '$40k/month',
    timeToValue: '1 day'
  },
  testimonials: [
    {
      companyName: 'TechSales Pro',
      authorName: 'Michael Thompson',
      authorTitle: 'VP of Sales',
      quote: 'Demo scheduling automation increased our qualified meeting rate by 73%. Our sales team now focuses on actual demos instead of calendar coordination.',
      metrics: '73% increase in qualified meetings'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Calendar Integration Setup',
      description: 'Connect your team calendars for seamless demo scheduling',
      url: '/docs/calendar-integration'
    },
    {
      type: 'template',
      title: 'Lead Qualification Scripts',
      description: 'Proven questions to qualify and route demo requests effectively',
      url: '/templates/demo-qualification'
    }
  ]
};